import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Clock, Users, BookOpen } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { AddButton, EditButton, DeleteButton } from '../components/AdminControls';
import EditDialog from '../components/EditDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import EditableText from '../components/EditableText';
import EditableInline from '../components/EditableInline';

const PageHeader = ({ eyebrow, title, subtitle }) => (
  <section className="bg-[#2a1a45] text-white py-20">
    <div className="max-w-4xl mx-auto px-6 text-center">
      {eyebrow && <p className="text-xs tracking-[0.25em] uppercase text-[#e6d4a8] mb-4">{eyebrow}</p>}
      <h1 className="font-display text-4xl md:text-6xl leading-tight text-white">{title}</h1>
      {subtitle && <p className="mt-5 text-[#d5c9a5] leading-relaxed">{subtitle}</p>}
    </div>
  </section>
);

const scheduleFields = (churchId) => ([
  { name: 'church_id', label: 'Church', type: 'select', required: true,
    options: [
      { value: 'st-marys', label: "St. Mary's (Ladysmith)" },
      { value: 'st-josephs', label: "St. Joseph's (Chemainus)" },
    ]
  },
  { name: 'day', label: 'Day', required: true, placeholder: 'Saturday' },
  { name: 'time', label: 'Time', required: true, placeholder: '5:00 PM' },
  { name: 'label', label: 'Label / Description', required: true, placeholder: 'Anticipated Sunday Mass' },
  { name: 'sort_order', label: 'Sort Order', type: 'number', placeholder: '1' },
]);

const MassTimes = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [churches, setChurches] = useState([]);
  const [editing, setEditing] = useState(null); // null | {churchId, mode:'new'} | scheduleItem
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    const { data } = await api.listSchedules();
    setChurches(data);
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (values) => {
    // Ensure sort_order is a number
    const payload = { ...values, sort_order: Number(values.sort_order || 0) };
    if (editing?.mode === 'new') {
      await api.createSchedule(payload);
      toast({ title: 'Schedule row added' });
    } else {
      await api.updateSchedule(editing.id, payload);
      toast({ title: 'Schedule row updated' });
    }
    await load();
  };

  const handleDelete = async () => {
    await api.deleteSchedule(deleteTarget.id);
    toast({ title: 'Schedule row deleted' });
    setDeleteTarget(null);
    await load();
  };

  return (
    <>
      <PageHeader
        eyebrow="Weekend & Weekday Schedule"
        title="Mass Times"
        subtitle="Join us for the celebration of the Holy Eucharist at either of our two parish churches. All are welcome."
      />

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-10">
          {churches.map(c => (
            <div key={c.id} className="bg-white border border-[#e9e0cc] p-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#f2ead6] flex items-center justify-center text-[#3a2359]">
                  <Users size={18} />
                </div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b]">{c.community}</p>
              </div>
              <h2 className="font-display text-3xl text-[#2a1a45] mb-4">{c.name}</h2>
              <div className="text-sm text-[#5a534a] space-y-1.5 mb-6">
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="mt-1 text-[#a68a5b]" />
                  <EditableInline
                    contentKey={`church_${c.id}_address`}
                    defaultValue={c.address}
                    label={`${c.name} address`}
                    placeholder="Street, City, Province Postal"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={14} className="text-[#a68a5b]" />
                  <EditableInline
                    as="a"
                    href="tel:auto"
                    contentKey={`church_${c.id}_phone`}
                    defaultValue={c.phone}
                    label={`${c.name} phone`}
                    className="hover:text-[#3a2359]"
                    placeholder="(250) 555-0000"
                  />
                </div>
              </div>
              <div className="border-t border-[#e9e0cc] pt-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#a68a5b] font-medium"><Clock size={14} /> Schedule</p>
                  {isAdmin && (
                    <AddButton onClick={() => setEditing({ mode: 'new', church_id: c.id })} label="Add" />
                  )}
                </div>
                <ul className="space-y-3">
                  {c.schedule.map((s) => (
                    <li key={s.id} className="flex items-baseline justify-between gap-4 pb-3 border-b border-[#f2ead6] last:border-0">
                      <div className="flex-1">
                        <p className="text-[#2a1a45] font-medium text-sm">{s.day}</p>
                        <p className="text-xs text-[#78716c]">{s.label}</p>
                      </div>
                      <span className="font-display text-xl text-[#3a2359] whitespace-nowrap">{s.time}</span>
                      {isAdmin && (
                        <div className="flex items-center gap-1 ml-2">
                          <EditButton onClick={() => setEditing(s)} />
                          <DeleteButton onClick={() => setDeleteTarget(s)} />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-8">
          <div className="bg-[#f2ead6]/50 border border-[#e9e0cc] p-8">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen size={22} className="text-[#3a2359]" />
              <h3 className="font-display text-2xl text-[#2a1a45]">Adoration</h3>
            </div>
            <EditableText
              contentKey="mass_times_adoration_body"
              label="Adoration Description"
              rows={5}
              defaultValue="Eucharistic Adoration is held on the First Friday of every month at St. Joseph's from 9:30 AM to 10:30 AM, following the morning Mass."
              paragraphClass="text-sm text-[#5a534a] leading-relaxed"
            />
          </div>
          <div className="bg-[#f2ead6]/50 border border-[#e9e0cc] p-8">
            <div className="flex items-center gap-3 mb-4">
              <Clock size={22} className="text-[#3a2359]" />
              <h3 className="font-display text-2xl text-[#2a1a45]">Holy Days of Obligation</h3>
            </div>
            <EditableText
              contentKey="mass_times_holy_days_body"
              label="Holy Days of Obligation Description"
              rows={5}
              defaultValue="Special Mass times are announced in the weekly bulletin. For pastoral emergencies outside office hours, please contact the parish office."
              paragraphClass="text-sm text-[#5a534a] leading-relaxed"
            />
          </div>
        </div>
      </section>

      <EditDialog
        open={!!editing}
        title={editing?.mode === 'new' ? 'Add Schedule Row' : 'Edit Schedule Row'}
        fields={scheduleFields()}
        initial={editing?.mode === 'new'
          ? { church_id: editing.church_id, day: '', time: '', label: '', sort_order: 0 }
          : editing || {}}
        onClose={() => setEditing(null)}
        onSubmit={handleSave}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this schedule row?"
        message={deleteTarget ? `“${deleteTarget.day} — ${deleteTarget.label}” will be permanently removed.` : ''}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default MassTimes;
export { PageHeader };
